import KybAccessRecertificationRepository from "@/core/kyb/repositories/KybAccessRecertificationRepository";
import {
  DecideKybAccessRecertificationsInput,
  KybAccessRecertification,
  KybAccessRecertificationOutcome,
  KybAccessRecertificationPhase,
  KybAccessRecertificationStatus,
  KybRecertificationSetting,
  KybRecertificationSettingInput,
} from "@/core/kyb/entities/KybAccessRecertification";
import { KybCorporateAccessStatus, KybCorporateMfaChallenge } from "@/core/kyb/entities/KybCorporateAccess";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest } from "../../http/services/kybRequest/kybRequest";
import kybAccessRecertificationEndpoints from "../../api/kybAccessRecertificationEndpoints/kybAccessRecertificationEndpoints";

type RecertificationDto = {
  recertificationId: string;
  accessId: string;
  caseId: string;
  businessKey?: string | null;
  legalEntityName?: string | null;
  holderName?: string | null;
  mandateId?: string | null;
  accessStatus?: KybCorporateAccessStatus | null;
  status: KybAccessRecertificationStatus;
  phase?: KybAccessRecertificationPhase | null;
  dueDate: string;
  graceUntil: string;
  decidedBy?: string | null;
  decidedAt?: string | null;
  justification?: string | null;
  reactivatedBy?: string | null;
  reactivatedAt?: string | null;
};

type SettingDto = {
  periodMonths: number;
  noticeDays: number;
  graceDays: number;
  custom: boolean;
  justification?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
};

const mapSetting = (dto: SettingDto): KybRecertificationSetting => ({
  periodMonths: dto.periodMonths,
  noticeDays: dto.noticeDays,
  graceDays: dto.graceDays,
  custom: dto.custom,
  justification: orUndefined(dto.justification),
  updatedBy: orUndefined(dto.updatedBy),
  updatedAt: orUndefined(dto.updatedAt),
});

type OutcomeDto = { recertificationId: string; status: KybAccessRecertificationStatus; replayed: boolean };

/** Le back rend `null` pour une valeur absente : l'écran travaille avec des champs optionnels. */
const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

const mapRecertification = (dto: RecertificationDto): KybAccessRecertification => ({
  recertificationId: dto.recertificationId,
  accessId: dto.accessId,
  caseId: dto.caseId,
  businessKey: orUndefined(dto.businessKey),
  legalEntityName: orUndefined(dto.legalEntityName),
  holderName: orUndefined(dto.holderName),
  mandateId: orUndefined(dto.mandateId),
  accessStatus: orUndefined(dto.accessStatus),
  status: dto.status,
  phase: orUndefined(dto.phase),
  dueDate: dto.dueDate,
  graceUntil: dto.graceUntil,
  decidedBy: orUndefined(dto.decidedBy),
  decidedAt: orUndefined(dto.decidedAt),
  justification: orUndefined(dto.justification),
  reactivatedBy: orUndefined(dto.reactivatedBy),
  reactivatedAt: orUndefined(dto.reactivatedAt),
});

const mapOutcome = (dto: OutcomeDto): KybAccessRecertificationOutcome => ({
  recertificationId: dto.recertificationId,
  status: dto.status,
  replayed: dto.replayed,
});

export default class KybAccessRecertificationRepositoryBackend implements KybAccessRecertificationRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async list(statuses: KybAccessRecertificationStatus[] = []): Promise<RepositoryResult<KybAccessRecertification[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<RecertificationDto[]>>({
          url: kybAccessRecertificationEndpoints.list(statuses),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapRecertification),
    );
  }

  async decide(input: DecideKybAccessRecertificationsInput): Promise<RepositoryResult<KybAccessRecertificationOutcome[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<OutcomeDto[]>, DecideKybAccessRecertificationsInput>({
          url: kybAccessRecertificationEndpoints.decisions,
          body: input,
          withAuth: true,
        }),
      (dtos) => dtos.map(mapOutcome),
    );
  }

  async getSettings(): Promise<RepositoryResult<KybRecertificationSetting>> {
    return executeKybRequest(
      () => this.httpClient.get<ApiResponse<SettingDto>>({ url: kybAccessRecertificationEndpoints.settings, withAuth: true }),
      mapSetting,
    );
  }

  async updateSettings(input: KybRecertificationSettingInput): Promise<RepositoryResult<KybRecertificationSetting>> {
    return executeKybRequest(
      () =>
        this.httpClient.put<ApiResponse<SettingDto>, KybRecertificationSettingInput>({
          url: kybAccessRecertificationEndpoints.settings,
          body: input,
          withAuth: true,
        }),
      mapSetting,
    );
  }

  async requestMfaChallenge(): Promise<RepositoryResult<KybCorporateMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<{ sentTo: string; ttlMinutes: number; testCode?: string }>, undefined>({
          url: kybAccessRecertificationEndpoints.mfaChallenge,
          withAuth: true,
        }),
      (dto) => ({ sentTo: dto.sentTo, ttlMinutes: dto.ttlMinutes, testCode: dto.testCode }),
    );
  }

  async reactivate(
    recertificationId: string,
    justification: string,
    otpCode?: string,
  ): Promise<RepositoryResult<KybAccessRecertificationOutcome>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<OutcomeDto>, { justification: string; otpCode?: string }>({
          url: kybAccessRecertificationEndpoints.reactivation(recertificationId),
          body: { justification, otpCode },
          withAuth: true,
        }),
      mapOutcome,
    );
  }
}
